/**
 * Mappers for converting GraphQL membership types to legacy interface types
 *
 * These mappers enable gradual migration by allowing GraphQL data to be used
 * with existing UI components that expect the legacy interface types.
 */

import type {
  MembershipGraphQL,
  PerkGraphQL,
  MembershipPriceGraphQL,
} from './useMembershipsRelay';
import type { UserMembershipGraphQL } from './useUserMembershipsRelay';
import type {
  IDocument,
  IMembership,
  IMembershipPerk,
  IPrice,
  IUserMembership,
  IMembershipData,
} from '../../../../ts/interface/membership.interface';
import {
  isoDurationToSeconds,
  secondsToInterval,
  type Interval,
} from '../../../../util/isoDuration';

/**
 * Parse the renewalInterval ISO 8601 duration into the value + units the
 * pricing table and form use. ant serializes the stored interval, which the
 * admin saves in seconds, so most arrive in hours: "PT24H" -> 1 day,
 * "PT720H" -> 1 month; "P1M" / "P1Y" come from intervals stored as months.
 */
function parseRenewalInterval(interval?: string | null): Interval {
  return secondsToInterval(isoDurationToSeconds(interval));
}

/**
 * Determine pricing type based on GraphQL price data
 */
function determinePricingType(price: MembershipPriceGraphQL): string {
  if (price.termStartsOn || price.termEndsOn) {
    return 'date_range';
  }
  return 'interval';
}

/**
 * Map GraphQL MembershipPrice to legacy IPrice interface
 */
export function mapMembershipPriceToLegacy(price: MembershipPriceGraphQL): IPrice {
  const { intervalValue, intervalUnits } = parseRenewalInterval(price.renewalInterval);

  return {
    id: price.id,
    price: price.price ?? 0,
    priority: String(price.priority),
    autoRenew: !!price.autoRenews,
    pricingType: determinePricingType(price),
    starts: price.termStartsOn ?? '',
    ends: price.termEndsOn ?? '',
    intervalUnits,
    intervalValue,
  };
}

/**
 * Map GraphQL Perk to legacy IMembershipPerk interface
 */
export function mapPerkToLegacy(
  perk: PerkGraphQL,
  membershipId?: string
): IMembershipPerk {
  return {
    id: perk.oldId ?? perk.id,
    uuid: perk.id,
    active_on: perk.activeOn ?? null,
    archived_on: perk.archivedOn ?? null,
    color_primary: '',
    created_on: perk.createdOn,
    currency_code: perk.system?.currencyCode ?? '',
    discount: perk.discount ?? 0,
    free_time: perk.freeTime ?? 0,
    membership_id: membershipId ?? '',
    membership_uuid: '',
    mobility_type: perk.system?.mobilityType ?? 0,
    money_off: perk.moneyOff ?? 0,
    no_flex: perk.noFlex ?? false,
    reservation: perk.reservation ?? false,
    station_id: perk.locationId ?? null,
    station_name: perk.location?.name ?? null,
    system_id: perk.system?.oldId ?? null,
    system_name: perk.system?.name ?? null,
    system_uuid: perk.system?.id ?? null,
    waive_base_rate: perk.waiveBaseRate ?? false,
  };
}

/**
 * Normalize a GraphQL Json scalar value to an object. The API returns the
 * scalar as an already-parsed object, but older callers pass JSON strings.
 */
function toDataObject(data: unknown): Record<string, any> | null {
  if (!data) return null;
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      return typeof parsed === 'object' && parsed !== null ? parsed : null;
    } catch {
      return null;
    }
  }
  return typeof data === 'object' ? (data as Record<string, any>) : null;
}

/**
 * Parse the JSON data field to get documents
 */
function parseDataField(data?: unknown): IMembershipData {
  const parsed = toDataObject(data);
  return {
    docs: Array.isArray(parsed?.docs) ? parsed.docs : [],
  };
}

/**
 * Parse the user's uploaded documents from the user membership data field.
 * Unlike the membership plan's docs (a real array of requirements), the
 * uploaded docs are stored as a JSON-encoded string inside the data object.
 */
function parseUserMembershipDocs(data: unknown): IDocument[] {
  const docs = toDataObject(data)?.docs;
  if (Array.isArray(docs)) return docs;
  if (typeof docs === 'string') {
    try {
      const parsed = JSON.parse(docs);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

/**
 * Parse the user membership data JSON field for usage-related fields
 */
function parseUserMembershipData(data?: unknown): {
  email_auth_code: string | null;
  minutes_left: number | null;
  unlock_count: number;
  additional_unlocks: number;
} {
  const parsed = toDataObject(data);
  return {
    email_auth_code: parsed?.email_auth_code ?? null,
    minutes_left: parsed?.minutes_left ?? null,
    unlock_count: parsed?.unlock_count ?? 0,
    additional_unlocks: parsed?.additional_unlocks ?? 0,
  };
}

/**
 * Map GraphQL Membership to legacy IMembership interface
 */
export function mapMembershipToLegacy(membership: MembershipGraphQL): IMembership {
  return {
    id: membership.id,
    membership_id: membership.oldId ?? membership.id,
    name: membership.name,
    created: membership.createdOn,
    active_on: membership.activeOn ?? null,
    archived_on: membership.archivedOn ?? null,
    auth_type: membership.authType,
    auth_expiration: membership.authExpiration ?? null,
    password: membership.password ?? null,
    valid_emails: membership.validEmails ? [...membership.validEmails] : [],
    hide_valid_emails: membership.hideValidEmails,
    credit_card_hold: membership.creditCardHold,
    minimum_wallet_deposit: membership.minimumWalletDeposit,
    priority: membership.priority,
    max_rentals: membership.maxRentals,
    hidden: membership.hidden,
    color_primary: membership.brandingColor,
    logo_image_id: membership.logoImageId ? String(membership.logoImageId) : null,
    data: parseDataField(membership.data),
    membership_replaced: membership.replacementMembership?.name ?? null,
    perks: membership.perks.map((perk) =>
      mapPerkToLegacy(perk, membership.oldId ?? membership.id)
    ),
    prices: membership.prices.map(mapMembershipPriceToLegacy),
    system: membership.system
      ? {
          gid: membership.system.id,
          id: membership.system.oldId,
          name: membership.system.name,
          currencyCode: membership.system.currencyCode,
          mobilityType: membership.system.mobilityType,
        }
      : undefined,
    // These fields come from user membership context, not membership itself
    approved_on: null,
    canceled_on: null,
    expires_on: null,
    rejected: null,
  };
}

/**
 * Map GraphQL UserMembership to legacy IUserMembership interface
 */
export function mapUserMembershipToLegacy(
  userMembership: UserMembershipGraphQL
): IUserMembership {
  const membership = userMembership.membership;

  const user = userMembership.user;

  return {
    id: userMembership.id,
    oldId: userMembership.oldId ?? null,
    membership: membership
      ? {
          ...mapMembershipToLegacy(membership as unknown as MembershipGraphQL),
          // Override with user membership specific status fields
          approved_on: userMembership.approvedOn ?? null,
          canceled_on: userMembership.canceledOn ?? null,
          expires_on: userMembership.expiresOn ?? null,
          rejected: userMembership.rejectedOn ?? null,
        }
      : ({} as IMembership),
    user: user
      ? {
          full_name: user.fullName || `${user.firstName} ${user.lastName}`.trim(),
          phone: user.phone,
          email: user.email ?? null,
        }
      : undefined,
    email: user?.email ?? null,
    created_on: userMembership.createdOn,
    approved: userMembership.isApproved ?? false,
    canceled: userMembership.isCanceled ?? false,
    expired: userMembership.isExpired ?? false,
    active: userMembership.isActive ?? false,
    pending: userMembership.isPending ?? false,
    rejected: userMembership.isRejected ?? false,
    rejected_on: userMembership.rejectedOn ?? null,
    expires_on: userMembership.expiresOn ?? null,
    approved_on: userMembership.approvedOn ?? null,
    canceled_on: userMembership.canceledOn ?? null,
    last_charged: userMembership.lastCharged ?? null,
  };
}

/**
 * Map GraphQL UserMembership to a detailed object for the user membership detail page.
 * Includes fields beyond IUserMembership that the detail page uses.
 * Fields not available in GraphQL are set to null/0 and the page handles them gracefully.
 */
export function mapUserMembershipToDetailed(userMembership: UserMembershipGraphQL) {
  const membership = userMembership.membership;
  const user = userMembership.user;
  const mappedMembership = membership
    ? mapMembershipToLegacy(membership as unknown as MembershipGraphQL)
    : null;

  // Use the directly resolved membershipPrice from GraphQL, falling back to lookup by ID
  const membershipPrice = userMembership.membershipPrice
    ? userMembership.membershipPrice
    : userMembership.membershipPriceId && membership?.prices
    ? membership.prices.find((p) => p.id === userMembership.membershipPriceId)
    : null;

  // Parse the user membership data JSON for usage fields
  const parsedData = parseUserMembershipData(userMembership.data);

  // Compute renews_on: for active auto-renewing memberships, expiresOn is the next renewal date
  const renews_on =
    membershipPrice?.autoRenews && userMembership.expiresOn && userMembership.isActive
      ? userMembership.expiresOn
      : null;

  // Build canceled_by_admin object from the resolved admin user
  const canceledByAdminObj = userMembership.canceledByAdminUser
    ? {
        email: userMembership.canceledByAdminUser.email,
        full_name:
          `${userMembership.canceledByAdminUser.firstName} ${userMembership.canceledByAdminUser.lastName}`.trim(),
      }
    : null;

  return {
    id: userMembership.id,
    oldId: userMembership.oldId ?? null,
    membership: mappedMembership
      ? {
          ...mappedMembership,
          approved_on: userMembership.approvedOn ?? null,
          canceled_on: userMembership.canceledOn ?? null,
          expires_on: userMembership.expiresOn ?? null,
          rejected: userMembership.rejectedOn ?? null,
        }
      : null,
    user: user
      ? {
          full_name: user.fullName || `${user.firstName} ${user.lastName}`.trim(),
          phone: user.phone,
          emailAddress: user.email ?? null,
        }
      : undefined,
    email: userMembership.email ?? user?.email ?? null,
    email_address: user?.email ?? null,
    created_on: userMembership.createdOn,
    approved_on: userMembership.approvedOn ?? null,
    canceled_on: userMembership.canceledOn ?? null,
    rejected_on: userMembership.rejectedOn ?? null,
    expires_on: userMembership.expiresOn ?? null,
    renewed_times: userMembership.renewedTimes,
    active: userMembership.isActive ?? false,
    canceled: userMembership.isCanceled ?? false,
    expired: userMembership.isExpired ?? false,
    system: { id: userMembership.systemId },
    membership_price: membershipPrice
      ? {
          price: membershipPrice.price ?? 0,
          // Seconds, for getFormattedMembershipPrice ("PT24H" -> 86400).
          interval: isoDurationToSeconds(membershipPrice.renewalInterval),
          starts_on: membershipPrice.termStartsOn,
          ends_on: membershipPrice.termEndsOn,
          autoRenews: membershipPrice.autoRenews,
        }
      : null,
    unlocks: membershipPrice?.unlocks ?? null,
    data: { docs: parseUserMembershipDocs(userMembership.data) },
    email_auth_code: parsedData.email_auth_code,
    last_charged_on: userMembership.lastCharged ?? null,
    rejection_reason: userMembership.rejectionReason ?? null,
    renews_on,
    canceled_by_admin: canceledByAdminObj,
    admin_cancellation_reason: userMembership.adminCancellationReason ?? null,
    cancelation_type: userMembership.cancelationType ?? null,
    minutes_left: parsedData.minutes_left,
    minutes_total: membershipPrice?.minutes ?? null,
    unlock_count: parsedData.unlock_count ?? 0,
    additional_unlocks: parsedData.additional_unlocks ?? 0,
    free_renewals: userMembership.freeRenewals,
  };
}

/**
 * Map an array of GraphQL memberships to legacy format
 */
export function mapMembershipsToLegacy(memberships: MembershipGraphQL[]): IMembership[] {
  return memberships.map(mapMembershipToLegacy);
}

/**
 * Map an array of GraphQL user memberships to legacy format
 */
export function mapUserMembershipsToLegacy(
  userMemberships: UserMembershipGraphQL[]
): IUserMembership[] {
  return userMemberships.map(mapUserMembershipToLegacy);
}

/**
 * Convert legacy filter to GraphQL filter format
 */
export function convertFilterToGraphQL(filter: {
  membership_id?: string;
  system_id?: string;
  organization_id?: string | null;
  pattern?: string | null;
  status?: string;
}): {
  membershipId?: string;
  systemId?: string;
  organizationId?: string;
  name?: string;
  status?: 'active' | 'archived' | 'all';
} {
  const result: {
    membershipId?: string;
    systemId?: string;
    organizationId?: string;
    name?: string;
    status?: 'active' | 'archived' | 'all';
  } = {};

  if (filter.membership_id) {
    result.membershipId = filter.membership_id;
  }
  if (filter.system_id) {
    result.systemId = filter.system_id;
  }
  if (filter.organization_id) {
    result.organizationId = filter.organization_id;
  }
  if (filter.pattern) {
    result.name = filter.pattern;
  }
  if (filter.status) {
    const statusLower = filter.status.toLowerCase();
    if (statusLower === 'active' || statusLower === 'archived' || statusLower === 'all') {
      result.status = statusLower;
    }
  }

  return result;
}
